import React from 'react';
import { Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';

/**
 * The reservation's id, on the card.
 *
 * Every support conversation about a reservation starts with it — it is what the URL of a failing
 * request carries, what the logs key on, and what an operator has to read back over the phone —
 * and the card showed everything except that.
 *
 * Shortened to the first eight characters because that is what a person can read aloud and it is
 * already unique enough to find the row; the whole id is one click away on the clipboard, and in
 * the tooltip for anyone who would rather select it by hand.
 */

export interface BookingIdChipProps {
  bookingId: string;
  lightTheme?: boolean;
  className?: string;
}

const BookingIdChip: React.FC<BookingIdChipProps> = ({ bookingId, lightTheme = true, className }) => {
  if (!bookingId) return null;

  const short = bookingId.slice(0, 8).toUpperCase();

  const copy = () => {
    navigator.clipboard?.writeText(bookingId)
      .then(() => toast.success('ID da reserva copiado'))
      .catch(() => toast.error('Não foi possível copiar'));
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`Reserva ${bookingId} — clique para copiar`}
      className={cn(
        'inline-flex items-center gap-1 font-mono text-[11px] tracking-tight rounded px-1 py-0.5 transition-colors',
        lightTheme ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600' : 'text-gray-500 hover:bg-gray-800 hover:text-gray-300',
        className,
      )}
    >
      #{short}
      <Copy className="h-2.5 w-2.5" />
    </button>
  );
};

export default BookingIdChip;
